import { useState, type FormEvent, type ReactNode } from 'react';
import {
  CheckCircle,
  ClockCountdown,
  Copy,
  HandPalm,
  Lightning,
  PlugsConnected,
  Question,
  SpinnerGap,
  X,
} from '@phosphor-icons/react';
import { useCreateRefund, type RefundReason } from '@/lib/refunds/useRefunds';
import type { StatementLineItem } from '@/lib/billing/useBillingStatement';
import { cn } from '@/lib/utils/cn';

// Reasons mirror the b2b_refund_requests_reason_check constraint — the same
// vocabulary F1Jobs reviews refunds with. Approval waives the setup's charge
// on the statement.
const REASONS: { value: RefundReason; label: string; hint: string; icon: ReactNode }[] = [
  {
    value: 'session_not_started',
    label: 'Session never started',
    hint: 'The candidate never launched this interview session.',
    icon: <ClockCountdown className="h-3.5 w-3.5" />,
  },
  {
    value: 'technical_issue',
    label: 'Technical issue',
    hint: 'App, audio, or connectivity problems disrupted the session.',
    icon: <PlugsConnected className="h-3.5 w-3.5" />,
  },
  {
    value: 'very_short_session',
    label: 'Very short session',
    hint: 'The session ended almost immediately after starting.',
    icon: <Lightning className="h-3.5 w-3.5" />,
  },
  {
    value: 'duplicate_charge',
    label: 'Duplicate charge',
    hint: 'This setup duplicates another setup for the same interview.',
    icon: <Copy className="h-3.5 w-3.5" />,
  },
  {
    value: 'wrong_session',
    label: 'Wrong session',
    hint: 'Set up by mistake or for the wrong candidate.',
    icon: <HandPalm className="h-3.5 w-3.5" />,
  },
  {
    value: 'other',
    label: 'Other',
    hint: 'Something else — please explain below.',
    icon: <Question className="h-3.5 w-3.5" />,
  },
];

export function RequestWaiverDialog({
  item,
  currency,
  onClose,
}: {
  item: StatementLineItem;
  currency: string;
  onClose: () => void;
}) {
  const createRefund = useCreateRefund();
  const [reason, setReason] = useState<RefundReason>('technical_issue');
  const [details, setDetails] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const detailsRequired = reason === 'other';

  function close() {
    if (createRefund.isPending) return;
    onClose();
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (detailsRequired && details.trim().length < 10) {
      setError('Please describe what happened (at least 10 characters).');
      return;
    }
    try {
      await createRefund.mutateAsync({
        session_id: item.session_id,
        reason,
        reason_details: details.trim() || null,
        session_amount: item.amount,
        session_type: item.session_type,
      });
      setSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.');
    }
  }

  return (
    <div
      data-testid="request-waiver-dialog"
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Request waiver"
    >
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={close}
        aria-hidden="true"
      />

      <div className="relative w-full max-w-lg rounded-2xl border border-white/10 bg-neurax-midnight p-6 shadow-glow">
        <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-neurax-amber/50 via-neurax-electric/50 to-transparent" />

        <button
          type="button"
          onClick={close}
          disabled={createRefund.isPending}
          className="absolute right-4 top-4 rounded-lg p-1.5 text-muted-foreground transition hover:bg-white/5 hover:text-white disabled:opacity-50"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="mb-4 flex items-center gap-3">
          <div className="grid h-10 w-10 place-items-center rounded-xl bg-gradient-to-br from-neurax-amber/30 to-neurax-electric/30 text-neurax-amber">
            <HandPalm className="h-5 w-5" />
          </div>
          <div>
            <h2 className="font-display text-lg font-semibold text-white">Request waiver</h2>
            <p className="text-xs text-muted-foreground">
              F1Jobs reviews every request within a business day. Approved charges drop off your
              statement automatically.
            </p>
          </div>
        </div>

        {/* The charge being disputed */}
        <div
          data-testid="waiver-item-summary"
          className="mb-5 flex items-center justify-between gap-3 rounded-xl border border-white/5 bg-white/[0.02] px-4 py-3"
        >
          <div className="min-w-0">
            <p className="truncate text-sm font-medium text-white">{item.end_client_name}</p>
            <p className="truncate text-[11px] text-muted-foreground">
              {[item.position_title, item.company_name].filter(Boolean).join(' · ') ||
                (item.session_type === 'online_assessment' ? 'Assessment' : 'Interview')}
            </p>
          </div>
          <p className="shrink-0 font-display text-sm font-semibold text-white">
            {fmtAmount(item.amount, currency)}
          </p>
        </div>

        {success ? (
          <div data-testid="waiver-success" className="space-y-4">
            <div className="flex items-start gap-3 rounded-xl border border-neurax-mint/20 bg-neurax-mint/10 p-4 text-sm text-neurax-mint">
              <CheckCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                Waiver requested. You can track its status in the Refund requests section below —
                the line stays on your statement until it's approved.
              </span>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="w-full rounded-xl bg-gradient-to-r from-neurax-electric to-neurax-plasma px-4 py-2.5 text-sm font-semibold text-white shadow-glow-soft transition hover:brightness-110"
            >
              Done
            </button>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Reason
              </label>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {REASONS.map((r) => (
                  <button
                    key={r.value}
                    type="button"
                    data-testid={`waiver-reason-${r.value}`}
                    onClick={() => setReason(r.value)}
                    disabled={createRefund.isPending}
                    className={cn(
                      'rounded-xl border p-3 text-left transition disabled:opacity-50',
                      reason === r.value
                        ? 'border-neurax-electric/60 bg-neurax-electric/10'
                        : 'border-white/10 bg-white/[0.02] hover:border-white/20'
                    )}
                  >
                    <span
                      className={cn(
                        'flex items-center gap-1.5 text-sm font-medium',
                        reason === r.value ? 'text-white' : 'text-white/80'
                      )}
                    >
                      {r.icon}
                      {r.label}
                    </span>
                    <span className="mt-0.5 block text-[11px] leading-snug text-muted-foreground">
                      {r.hint}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Details{detailsRequired ? ' (required)' : ' (optional)'}
              </label>
              <textarea
                data-testid="waiver-details"
                value={details}
                onChange={(e) => setDetails(e.target.value)}
                rows={3}
                placeholder="Anything that helps us review this quickly…"
                disabled={createRefund.isPending}
                className="w-full resize-none rounded-xl border border-white/10 bg-white/[0.04] px-4 py-2.5 text-sm text-white placeholder:text-muted-foreground/60 outline-none transition focus:border-neurax-electric/60 focus:bg-white/[0.06] focus:ring-2 focus:ring-neurax-electric/30 disabled:opacity-50"
              />
            </div>

            {error && (
              <div
                role="alert"
                className="rounded-xl border border-destructive/30 bg-destructive/10 px-4 py-2.5 text-sm text-destructive"
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              data-testid="waiver-submit"
              disabled={createRefund.isPending}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-neurax-electric to-neurax-plasma px-4 py-2.5 text-sm font-semibold text-white shadow-glow-soft transition hover:brightness-110 disabled:opacity-60"
            >
              {createRefund.isPending && <SpinnerGap className="h-4 w-4 animate-spin" />}
              Submit waiver request
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

function fmtAmount(n: number, ccy: string) {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: ccy || 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(n);
  } catch {
    return `$${n.toFixed(2)}`;
  }
}
